import { useState, useEffect, useCallback, useRef } from 'react';
import { createLogger } from "@/lib/logger";
const log = createLogger("e2ee-chat");
import { base64ToArrayBuffer as b642ab } from '@/lib/crypto-utils';
import { supabase } from '@/integrations/supabase/client';
import { createManagedChannel } from '@/lib/realtime-reconnect';
import { showMessageNotification, getMessageTypePreview } from '@/lib/chat-notifications';
import {
  generateKeyPair,
  deriveSharedSecret,
  encryptMessage,
  decryptMessage,
  storeKeyPair,
  getStoredPrivateKey,
  getStoredPublicKey,
  hasStoredKeys,
} from '@/lib/encryption';
import {
  getOrCreateIdentity,
  exportPreKeyBundleBlob,
  exportIdentityKey,
  importPreKeyBundleBlob,
  restoreSession,
  initiateSession,
  acceptSession as acceptRatchetSession,
  ratchetEncrypt,
  ratchetDecrypt,
} from '@/lib/double-ratchet';
import type { RatchetEncryptResult } from '@/lib/double-ratchet';
import { PreKeyBundleProtocol, PreKeyMessageProtocol } from '2key-ratchet';
import type { AsymmetricRatchet, Identity } from '2key-ratchet';
import { getMediaUrl } from '@/hooks/useMediaUpload';
import { hasIdentityBackup } from '@/lib/identity-backup';
import { cacheDecryptedMessages, getCachedMessagesMap, clearAllLocalMessages, type LocalMessage } from '@/lib/local-message-store';
import {
  queueMessage,
  getAllPending,
  removePending,
  updatePendingStatus,
  getBackoffDelay,
  MAX_RETRIES,
  type PendingMessage,
} from '@/lib/offline-message-queue';
import type { User } from '@supabase/supabase-js';

interface EncryptedMessage {
  id: string;
  room_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  is_encrypted: boolean;
  message_type: string | null;
  file_url?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  file_type?: string | null;
  duration_seconds?: number | null;
  thumbnail_url?: string | null;
  reply_to_id?: string | null;
  read_at?: string | null;
  reactions?: any | null;
  expires_at?: string | null;
  /** Per-file decryption key (Signal-grade encrypted media) */
  file_key?: string | null;
  /** Encryption version: null/1 = legacy ECDH, 2 = double ratchet */
  encryption_version?: number | null;
}

interface ChatRoom {
  id: string;
  name: string | null;
  is_encrypted: boolean;
  room_type_v2: string;
  created_at: string;
  other_user?: {
    user_id: string;
    full_name: string | null;
    avatar_url: string | null;
    phone_number?: string | null;
  };
  last_message?: EncryptedMessage;
  unread_count?: number;
}

interface SendMessageOptions {
  content: string;
  messageType?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  durationSeconds?: number;
  thumbnailUrl?: string;
  replyToId?: string;
  /** Per-file AES key (Signal-grade media encryption) */
  fileKey?: string;
  /** Disappearing messages: ISO timestamp when message should expire */
  expiresAt?: string | null;
}

interface UseEncryptedChatReturn {
  rooms: ChatRoom[];
  messages: EncryptedMessage[];
  isLoading: boolean;
  error: string | null;
  currentRoom: ChatRoom | null;
  sendMessage: (content: string, options?: Partial<SendMessageOptions> & { targetRoomId?: string }) => Promise<void>;
  createDirectRoom: (otherUserId: string) => Promise<ChatRoom | null>;
  selectRoom: (roomId: string) => void;
  markAsRead: (roomId: string) => Promise<void>;
  initializeEncryption: () => Promise<void>;
  isEncryptionReady: boolean;
  fetchOlderMessages: (roomId: string) => Promise<void>;
  hasMoreMessages: boolean;
  isLoadingMore: boolean;
  needsRecovery: 'setup' | 'restore' | null;
  setNeedsRecovery: (v: 'setup' | 'restore' | null) => void;
}

// Module-level shared secret cache - survives re-renders, doesn't trigger state updates
const sharedSecretCache = new Map<string, CryptoKey>();

// Track key_version per user to detect stale secrets
const keyVersionCache = new Map<string, number>();

// Module-level ratchet session cache
const ratchetSessionCache = new Map<string, AsymmetricRatchet>();

// ─── Module-level message cache (Signal-style) ────────────────
// Survives re-renders, enables instant room re-entry
const messageCache = new Map<string, EncryptedMessage[]>();

/** Clear cached messages for specific room IDs (used by delete flows) */
export function clearRoomMessageCache(roomIds: string[]) {
  for (const id of roomIds) {
    messageCache.delete(id);
  }
}

// Module-level identity ref
let cachedIdentity: Identity | null = null;

// ─── Call event preview helper ────────────────────────────────
function getCallEventPreview(content: string): string {
  try {
    const data = JSON.parse(content);
    const isVideo = data?.is_video === true;
    const status = data?.status;
    if (status === 'missed') return isVideo ? '📹 Missed video call' : '📞 Missed voice call';
    if (status === 'declined') return isVideo ? '📹 Declined video call' : '📞 Declined voice call';
    return isVideo ? '📹 Video call' : '📞 Voice call';
  } catch {
    return '📞 Call';
  }
}

// ─── Deduplicate rooms by phone number ───────────────────────
// When the same contact has multiple accounts (e.g. unverified + verified),
// multiple rooms appear for the same person. We merge them by keeping
// the room with the most recent message and combining unread counts.
function deduplicateRoomsByPhone(rooms: ChatRoom[]): ChatRoom[] {
  const phoneMap = new Map<string, ChatRoom>();

  // First pass: determine the best room per phone number
  for (const room of rooms) {
    const phone = room.other_user?.phone_number;
    if (!phone) continue;

    const existing = phoneMap.get(phone);
    if (!existing) {
      phoneMap.set(phone, room);
      continue;
    }

    // Keep the room with the most recent activity; merge unread counts
    const existingTime = existing.last_message?.created_at || existing.created_at;
    const currentTime = room.last_message?.created_at || room.created_at;
    const mergedUnread = (existing.unread_count || 0) + (room.unread_count || 0);

    if (currentTime > existingTime) {
      if (!room.other_user?.full_name && existing.other_user?.full_name) {
        room.other_user = { ...room.other_user!, full_name: existing.other_user.full_name };
      }
      room.unread_count = mergedUnread;
      phoneMap.set(phone, room);
    } else {
      if (!existing.other_user?.full_name && room.other_user?.full_name) {
        existing.other_user = { ...existing.other_user!, full_name: room.other_user.full_name };
      }
      existing.unread_count = mergedUnread;
    }
  }

  // Second pass: build result, replacing dupes with the winner at first occurrence
  const seen = new Set<string>();
  const result: ChatRoom[] = [];
  for (const room of rooms) {
    const phone = room.other_user?.phone_number;
    if (!phone) {
      result.push(room);
      continue;
    }
    if (seen.has(phone)) continue;
    seen.add(phone);
    result.push(phoneMap.get(phone)!);
  }

  // Clear messageCache for rooms that were merged away (prevents stale data leaking)
  const survivingIds = new Set(result.map(r => r.id));
  for (const room of rooms) {
    if (!survivingIds.has(room.id)) {
      messageCache.delete(room.id);
    }
  }

  return result;
}

// ─── Room cache (localStorage) for instant load ───────────────

function getRoomsCacheKey(userId: string): string {
  return `elefa-chat-rooms-cache:${userId}`;
}

function getCachedRooms(userId: string): ChatRoom[] {
  try {
    const raw = localStorage.getItem(getRoomsCacheKey(userId));
    if (!raw) return [];
    return JSON.parse(raw);
  } catch { return []; }
}

function setCachedRooms(userId: string, rooms: ChatRoom[]) {
  try {
    // #7 FIX: Strip all last_message metadata (sender_id, created_at) for zero-knowledge
    const minimal = rooms.map(r => ({
      id: r.id,
      name: r.name,
      is_encrypted: r.is_encrypted,
      room_type_v2: r.room_type_v2,
      created_at: r.created_at,
      other_user: r.other_user,
      unread_count: r.unread_count,
      // Don't cache last_message at all - metadata leaks who/when (zero-knowledge violation)
    }));
    localStorage.setItem(getRoomsCacheKey(userId), JSON.stringify(minimal));
  } catch { /* quota exceeded - non-critical */ }
}

// Clear all module-level caches on user change (security: prevents ghost accounts)
function clearAllCaches() {
  sharedSecretCache.clear();
  keyVersionCache.clear();
  ratchetSessionCache.clear();
  messageCache.clear();
  cachedIdentity = null;
}

export function useEncryptedChat(options?: { enabled?: boolean }): UseEncryptedChatReturn {
  const enabled = options?.enabled ?? true;

  // ── Full no-op when disabled: prevent Supabase subscriptions, key ops, ratchet init ──
  const noopReturn: UseEncryptedChatReturn = {
    rooms: [],
    messages: [],
    isLoading: false,
    error: null,
    currentRoom: null,
    sendMessage: async () => {},
    createDirectRoom: async () => null,
    selectRoom: () => {},
    markAsRead: async () => {},
    initializeEncryption: async () => {},
    isEncryptionReady: false,
    fetchOlderMessages: async () => {},
    hasMoreMessages: false,
    isLoadingMore: false,
    needsRecovery: null,
    setNeedsRecovery: () => {},
  };

  // Do NOT initialize from cache - wait for auth to resolve to avoid ghost accounts
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [messages, setMessages] = useState<EncryptedMessage[]>([]);
  // #1 FIX: messagesRef for stale-closure-safe dedup in realtime handlers
  const messagesRef = useRef<EncryptedMessage[]>([]);
  messagesRef.current = messages;
  const [currentRoom, setCurrentRoom] = useState<ChatRoom | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEncryptionReady, setIsEncryptionReady] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [needsRecovery, setNeedsRecovery] = useState<'setup' | 'restore' | null>(null);

  // Cache authenticated user in a ref - fetched once, kept fresh via onAuthStateChange
  const userRef = useRef<User | null>(null);

  useEffect(() => {
    if (!enabled) return;
    // Use getSession (reads localStorage, no network) for instant auth
    supabase.auth.getSession().then(({ data }) => {
      userRef.current = data.session?.user ?? null;
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const prevUserId = userRef.current?.id;
      const newUserId = session?.user?.id;
      userRef.current = session?.user ?? null;

      // User changed or signed out - purge all caches to prevent ghost accounts
      if ((prevUserId && prevUserId !== newUserId) || _event === 'SIGNED_OUT') {
        clearAllCaches();
        // Wipe IndexedDB stores (Signal-grade secure deletion)
        clearAllLocalMessages();
        setRooms([]);
        setMessages([]);
        setCurrentRoom(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const getUser = () => userRef.current;

  // ─── Ratchet session management ─────────────────────────────

  const getRatchetSession = useCallback(async (
    myUserId: string,
    otherUserId: string,
  ): Promise<AsymmetricRatchet | null> => {
    const cacheKey = `${myUserId}::${otherUserId}`;

    // Check cache first
    if (ratchetSessionCache.has(cacheKey)) {
      return ratchetSessionCache.get(cacheKey)!;
    }

    // Try restoring from IndexedDB
    if (!cachedIdentity) {
      cachedIdentity = await getOrCreateIdentity(myUserId);
    }

    const session = await restoreSession(myUserId, otherUserId, cachedIdentity);
    if (session) {
      ratchetSessionCache.set(cacheKey, session);
    }
    return session;
  }, []);

  const getOrCreateRatchetSession = useCallback(async (
    myUserId: string,
    otherUserId: string,
  ): Promise<AsymmetricRatchet | null> => {
    // Check existing
    const existing = await getRatchetSession(myUserId, otherUserId);
    if (existing) return existing;

    // No session - need to initiate one using other user's prekey bundle
    if (!cachedIdentity) {
      cachedIdentity = await getOrCreateIdentity(myUserId);
    }

    // Fetch their prekey bundle from DB
    const { data: preKeyData } = await supabase
      .from('user_prekeys')
      .select('*')
      .eq('user_id', otherUserId)
      .single();

    if (!preKeyData) {
      log.warn('[DoubleRatchet] No prekey bundle for user', otherUserId, '- falling back to legacy');
      return null;
    }

    try {
      // Build a PreKeyBundleProtocol from the stored data
      const bundle = await buildPreKeyBundle(preKeyData);
      const session = await initiateSession(cachedIdentity, myUserId, otherUserId, bundle);
      const cacheKey = `${myUserId}::${otherUserId}`;
      ratchetSessionCache.set(cacheKey, session);

      // Consume one one-time prekey from the bundle
      const otpks = (preKeyData.one_time_prekeys as string[]) || [];
      if (otpks.length > 0) {
        const remaining = otpks.slice(1);
        await supabase
          .from('user_prekeys')
          .update({ one_time_prekeys: remaining, updated_at: new Date().toISOString() })
          .eq('user_id', otherUserId);
      }

      log.debug('[DoubleRatchet] Session initiated with', otherUserId);
      return session;
    } catch (err) {
      log.error('[DoubleRatchet] Failed to initiate session:', err);
      return null;
    }
  }, [getRatchetSession]);

  // Accept an incoming ratchet session from a PreKeyMessage
  const acceptIncomingSession = useCallback(async (
    myUserId: string,
    otherUserId: string,
    ciphertextB64: string,
  ): Promise<{ session: AsymmetricRatchet; plaintext: string } | null> => {
    if (!cachedIdentity) {
      cachedIdentity = await getOrCreateIdentity(myUserId);
    }

    try {
      const raw = b642ab(ciphertextB64);
      const preKeyMsg = await PreKeyMessageProtocol.importProto(raw);
      const session = await acceptRatchetSession(cachedIdentity, myUserId, otherUserId, preKeyMsg as PreKeyMessageProtocol);

      // Decrypt the inner message
      const plaintext = await ratchetDecrypt(session, ciphertextB64);

      const cacheKey = `${myUserId}::${otherUserId}`;
      ratchetSessionCache.set(cacheKey, session);

      log.debug('[DoubleRatchet] Session accepted from', otherUserId);
      return { session, plaintext };
    } catch (err) {
      log.error('[DoubleRatchet] Failed to accept session:', err);
      return null;
    }
  }, []);

  // ─── Initialize encryption (legacy + ratchet) ───────────────

  const initializeEncryption = useCallback(async () => {
    try {
      const user = getUser();
      if (!user) return;

      // --- Legacy ECDH keys ---
      const keysExist = await hasStoredKeys();
      log.debug('[E2EE] initializeEncryption: keysExist =', keysExist);
      
      if (!keysExist) {
        const { publicKey, privateKey } = await generateKeyPair();
        await storeKeyPair(publicKey, privateKey);
        
        const { data: existing } = await supabase
          .from('encryption_keys')
          .select('key_version')
          .eq('user_id', user.id)
          .single();
        
        const newVersion = (existing?.key_version ?? 0) + 1;
        log.debug('[E2EE] Generating new keys, version:', newVersion);
        
        const { error: upsertError } = await supabase.from('encryption_keys').upsert({
          user_id: user.id,
          public_key: publicKey,
          key_version: newVersion,
        }, { onConflict: 'user_id' });
        
        if (upsertError) {
          log.error('[E2EE] Failed to upsert public key to DB:', upsertError);
        }
        
        sharedSecretCache.clear();
        keyVersionCache.clear();
      } else {
        const localPublicKey = await getStoredPublicKey();
        if (localPublicKey) {
          const { data: existing } = await supabase
            .from('encryption_keys')
            .select('public_key, key_version')
            .eq('user_id', user.id)
            .single();
          
          if (!existing) {
            log.debug('[E2EE] No DB record, inserting public key');
            await supabase.from('encryption_keys').insert({
              user_id: user.id,
              public_key: localPublicKey,
              key_version: 1,
            });
          } else if (existing.public_key !== localPublicKey) {
            const newVersion = (existing.key_version ?? 0) + 1;
            log.warn('[E2EE] Key mismatch! Re-uploading, version:', newVersion);
            
            await supabase.from('encryption_keys').upsert({
              user_id: user.id,
              public_key: localPublicKey,
              key_version: newVersion,
            }, { onConflict: 'user_id' });
            
            sharedSecretCache.clear();
            keyVersionCache.clear();
          }
        }
      }

      // --- Double Ratchet identity + prekeys ---
      try {
        // Check if identity already exists locally (device continuity check)
        const { getIdentity } = await import('@/lib/double-ratchet');
        const existingLocalIdentity = await getIdentity(user.id);
        const isNewDevice = !existingLocalIdentity;

        // If new device, check if server backup exists → prompt restore
        if (isNewDevice) {
          const backupExists = await hasIdentityBackup(user.id);
          if (backupExists) {
            setNeedsRecovery('restore');
            log.debug('[IdentityBackup] Backup found - prompting user to restore');
          }
        }

        cachedIdentity = await getOrCreateIdentity(user.id);
        const bundleBlob = await exportPreKeyBundleBlob(cachedIdentity);
        const identityKeyB64 = await exportIdentityKey(cachedIdentity);

        // Check if prekeys already exist
        const { data: existingPrekeys } = await supabase
          .from('user_prekeys')
          .select('id, one_time_prekeys')
          .eq('user_id', user.id)
          .single();

        if (!existingPrekeys) {
          // Upload initial prekey bundle (stored as single protobuf blob)
          await supabase.from('user_prekeys').insert({
            user_id: user.id,
            signed_prekey: bundleBlob,
            signed_prekey_signature: '',
            one_time_prekeys: [],
          });

          // Store identity key in encryption_keys
          await supabase.from('encryption_keys').update({
            identity_key: identityKeyB64,
          }).eq('user_id', user.id);

          log.debug('[DoubleRatchet] Prekey bundle uploaded');
        } else {
          // C1: OTP replenishment - check if one_time_prekeys are depleted
          const otpks = (existingPrekeys.one_time_prekeys as string[]) || [];
          if (otpks.length < 20) {
            log.debug('[DoubleRatchet] OTP count low (', otpks.length, '), replenishing...');
            try {
              // Re-export a fresh bundle with new prekeys
              const freshBundle = await exportPreKeyBundleBlob(cachedIdentity);
              await supabase.from('user_prekeys').update({
                signed_prekey: freshBundle,
                updated_at: new Date().toISOString(),
              }).eq('user_id', user.id);
              log.debug('[DoubleRatchet] Prekey bundle replenished');
            } catch (replenishErr) {
              log.warn('[DoubleRatchet] OTP replenishment failed:', replenishErr);
            }
          } else {
            log.debug('[DoubleRatchet] Prekey bundle OK (', otpks.length, ' OTPs)');
          }
        }
      } catch (ratchetErr) {
        // Non-fatal: ratchet init failure shouldn't block legacy encryption
        log.error('[DoubleRatchet] Initialization failed (legacy still works):', ratchetErr);
      }

      // Prompt backup setup for users who haven't backed up yet (non-blocking)
      if (!needsRecovery) {
        try {
          const backupExists = await hasIdentityBackup(user.id);
          if (!backupExists) {
            setNeedsRecovery('setup');
          }
        } catch { /* non-fatal */ }
      }

      setIsEncryptionReady(true);
    } catch (err) {
      log.error('[E2EE] Failed to initialize encryption:', err);
      setError('Failed to initialize encryption');
    }
  }, []);

  // ─── Legacy shared secret (backward compat) ─────────────────

  const getSharedSecret = useCallback(async (otherUserId: string): Promise<CryptoKey | null> => {
    const myPrivateKey = await getStoredPrivateKey();
    if (!myPrivateKey) return null;

    const { data: keyData } = await supabase
      .from('encryption_keys')
      .select('public_key, key_version')
      .eq('user_id', otherUserId)
      .single();

    if (!keyData?.public_key) return null;

    const dbVersion = keyData.key_version ?? 1;
    const cachedVersion = keyVersionCache.get(otherUserId);

    if (sharedSecretCache.has(otherUserId) && cachedVersion === dbVersion) {
      return sharedSecretCache.get(otherUserId)!;
    }

    try {
      const secret = await deriveSharedSecret(myPrivateKey, keyData.public_key);
      sharedSecretCache.set(otherUserId, secret);
      keyVersionCache.set(otherUserId, dbVersion);
      return secret;
    } catch (err) {
      log.error('[E2EE] Failed to derive shared secret:', err);
      return null;
    }
  }, []);

  // Legacy decrypt with retry - falls back to local cache, never shows lock
  const decryptWithRetry = useCallback(async (
    content: string,
    otherUserId: string,
    secret: CryptoKey | null
  ): Promise<string> => {
    if (!secret) return content; // raw ciphertext - local cache will override later

    try {
      return await decryptMessage(content, secret);
    } catch {
      sharedSecretCache.delete(otherUserId);
      keyVersionCache.delete(otherUserId);
      
      const freshSecret = await getSharedSecret(otherUserId);
      if (!freshSecret) return content;

      try {
        return await decryptMessage(content, freshSecret);
      } catch {
        return content; // raw - local cache fallback applied in fetchMessages
      }
    }
  }, [getSharedSecret]);

  // ─── Unified decrypt: ratchet or legacy ──────────────────────

  const decryptContent = useCallback(async (
    msg: any,
    otherUserId: string,
    legacySecret: CryptoKey | null,
    myUserId: string,
  ): Promise<{ content: string; extraMeta: Record<string, any> }> => {
    const encVersion = msg.encryption_version;

    // Ratchet path (version 2)
    if (encVersion === 2) {
      try {
        let session = await getRatchetSession(myUserId, otherUserId);

        if (!session) {
          // This might be a PreKeyMessage - try accepting
          const result = await acceptIncomingSession(myUserId, otherUserId, msg.content);
          if (result) {
            return parseDecryptedContent(result.plaintext, msg);
          }
          // Return raw content instead of lock - message stays visible
          return { content: msg.content, extraMeta: {} };
        }

        const plaintext = await ratchetDecrypt(session, msg.content);
        return parseDecryptedContent(plaintext, msg);
      } catch (err) {
        log.error('[DoubleRatchet] Decrypt failed:', err);
        // Try accepting as new session (sender may have re-initialized)
        try {
          const result = await acceptIncomingSession(myUserId, otherUserId, msg.content);
          if (result) {
            return parseDecryptedContent(result.plaintext, msg);
          }
        } catch { /* fall through */ }
        // Return raw content instead of lock
        return { content: msg.content, extraMeta: {} };
      }
    }

    // Legacy path (version null/1)
    const raw = await decryptWithRetry(msg.content, otherUserId, legacySecret);
    return parseDecryptedContent(raw, msg);
  }, [getRatchetSession, acceptIncomingSession, decryptWithRetry]);

  // Track rooms the user has explicitly marked as read to prevent
  // stale poll data from restoring old unread counts.
  const markedReadRoomsRef = useRef<Set<string>>(new Set());

  // Stable ref for current room - used in fetchRooms to protect active room unread count
  const currentRoomRef = useRef(currentRoom);
  currentRoomRef.current = currentRoom;

  // ─── Fetch rooms ────────────────────────────────────────────

  const fetchRooms = useCallback(async () => {
    try {
      const user = getUser();
      if (!user) return;

      const { data, error: rpcError } = await supabase.rpc('get_user_rooms', {
        p_user_id: user.id,
      });

      if (rpcError) {
        log.error('Failed to fetch rooms:', rpcError);
        return;
      }

      const rawMapped: ChatRoom[] = (data || []).map((row: any) => ({
        id: row.room_id,
        name: row.room_name,
        is_encrypted: row.is_encrypted,
        room_type_v2: row.room_type_v2 || 'direct',
        created_at: row.room_created_at,
        other_user: row.other_user_id ? {
          user_id: row.other_user_id,
          full_name: row.other_user_name,
          avatar_url: row.other_user_avatar,
          phone_number: row.other_user_phone,
        } : undefined,
        last_message: row.last_message_at ? {
          id: '',
          room_id: row.room_id,
          sender_id: row.last_message_sender_id,
          content: row.last_message_type === 'call_event'
            ? getCallEventPreview(row.last_message_content)
            : row.is_encrypted ? '💬 Message' : row.last_message_content,
          created_at: row.last_message_at,
          is_encrypted: row.is_encrypted,
          message_type: row.last_message_type,
        } : undefined,
        unread_count: Number(row.unread_count) || 0,
      }));

      // Deduplicate rooms: if multiple rooms share the same other_user phone number,
      // keep only the one with the most recent activity (prevents duplicate contacts)
      const mapped = deduplicateRoomsByPhone(rawMapped);

      // Protect rooms the user just marked as read AND the currently active room
      const protectedRooms = markedReadRoomsRef.current;
      const activeRoomId = currentRoomRef.current?.id;
      const finalMapped = mapped.map(r => {
        if (protectedRooms.has(r.id) || r.id === activeRoomId) {
          return { ...r, unread_count: 0 };
        }
        return r;
      });

      setRooms(finalMapped);
      const uid = userRef.current?.id;
      if (uid) setCachedRooms(uid, finalMapped);

      // Prefetch shared secrets for all room partners (non-blocking)
      // Warms cache so room taps never wait for key derivation
      const otherUserIds = mapped
        .map(r => r.other_user?.user_id)
        .filter((id): id is string => !!id);
      if (otherUserIds.length > 0) {
        Promise.all(otherUserIds.map(id => getSharedSecret(id).catch(() => null)));
      }
    } catch (err) {
      log.error('Failed to fetch rooms:', err);
    }
  }, [getSharedSecret]);

  // ─── Fetch messages ─────────────────────────────────────────

  const fetchMessages = useCallback(async (roomId: string) => {
    try {
      // Guard: only fetch for the currently selected room to prevent cross-room leakage
      if (currentRoomRef.current && currentRoomRef.current.id !== roomId) {
        log.warn('[Chat] fetchMessages skipped - roomId mismatch with current room');
        return;
      }
      // #8 FIX: Event-driven auth wait instead of busy-wait loop
      let user = getUser();
      if (!user) {
        user = await new Promise<typeof user>((resolve) => {
          const timeout = setTimeout(() => {
            sub.unsubscribe();
            resolve(null);
          }, 3000);
          const { data: { subscription: sub } } = supabase.auth.onAuthStateChange((_evt, session) => {
            if (session?.user) {
              clearTimeout(timeout);
              sub.unsubscribe();
              userRef.current = session.user;
              resolve(session.user);
            }
          });
          // Check once more immediately (race condition)
          const current = getUser();
          if (current) { clearTimeout(timeout); sub.unsubscribe(); resolve(current); }
        });
        if (!user) {
          log.warn('[Chat] fetchMessages aborted - no auth after 3s');
          return;
        }
      }

      // Fetch messages, other member, and their last_read_at in parallel
      const [messagesResult, memberResult, otherMemberReadResult] = await Promise.all([
        supabase
          .from('chat_messages')
          .select('*')
          .eq('room_id', roomId)
          .not('is_deleted', 'is', 'true')
          .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
          .order('created_at', { ascending: false })
          .limit(30),
        supabase
          .from('chat_room_members')
          .select('user_id')
          .eq('room_id', roomId)
          .neq('user_id', user.id)
          .single(),
        supabase
          .from('chat_room_members')
          .select('last_read_at')
          .eq('room_id', roomId)
          .neq('user_id', user.id)
          .single(),
      ]);

      const messagesData = messagesResult.data;
      if (!messagesData) {
        // No data returned (possibly RLS blocked) - clear stale cache
        messageCache.delete(roomId);
        return;
      }
      
      // Reverse to ascending order for display
      messagesData.reverse();

      const otherMember = memberResult.data;
      const otherUserId = otherMember?.user_id;

      let legacySecret: CryptoKey | null = null;
      if (otherMember) {
        legacySecret = await getSharedSecret(otherMember.user_id);
      }

      // Show raw messages instantly (encrypted content as placeholder), then decrypt in background
      const rawMessages: EncryptedMessage[] = messagesData.map(msg => {
        const meta = (msg.metadata || {}) as any;
        return {
          ...msg,
          content: msg.is_encrypted ? '💬' : msg.content,
          file_url: meta.file_url || undefined,
          file_name: meta.file_name || undefined,
          file_type: meta.file_type || undefined,
          file_size: meta.file_size || undefined,
          duration_seconds: meta.duration_seconds || undefined,
          thumbnail_url: meta.thumbnail_url || undefined,
          reply_to_id: meta.reply_to_id || undefined,
        } as EncryptedMessage;
      });
      
      // Only show placeholders on initial load (no cached messages yet)
      const cachedForRoom = messageCache.get(roomId);
      if (!cachedForRoom || cachedForRoom.length === 0) {
        setMessages(rawMessages);
      }

      // Decrypt in parallel batches - legacy ECDH is stateless so fully parallelizable
      // Ratchet (v2) messages are processed sequentially for chain state correctness
      const ratchetMsgs = messagesData.filter(m => m.is_encrypted && m.encryption_version === 2);
      const legacyMsgs = messagesData.filter(m => m.is_encrypted && m.encryption_version !== 2);
      
      // Build decryption map: msgId -> { content, extraMeta }
      const decryptionMap = new Map<string, { content: string; extraMeta: Record<string, any> }>();

      // Fetch local cache for all encrypted messages (Signal-style on-device store)
      const encryptedIds = messagesData.filter(m => m.is_encrypted).map(m => m.id);
      const localCache = await getCachedMessagesMap(encryptedIds);

      // Legacy ECDH: fully parallel
      if (legacyMsgs.length > 0 && otherUserId) {
        const legacyResults = await Promise.all(
          legacyMsgs.map(msg => 
            decryptContent(msg, otherUserId, legacySecret, user.id)
              .catch(() => ({ content: msg.content, extraMeta: {} }))
          )
        );
        legacyMsgs.forEach((msg, i) => decryptionMap.set(msg.id, legacyResults[i]));
      }

      // Ratchet: sequential (chain state)
      for (const msg of ratchetMsgs) {
        if (!otherUserId) { decryptionMap.set(msg.id, { content: msg.content, extraMeta: {} }); continue; }
        try {
          const result = await decryptContent(msg, otherUserId, legacySecret, user.id);
          decryptionMap.set(msg.id, result);
        } catch {
          decryptionMap.set(msg.id, { content: msg.content, extraMeta: {} });
        }
      }

      // For any encrypted message where decryption returned raw ciphertext,
      // fall back to the local on-device cache (Signal pattern)
      for (const msg of messagesData) {
        if (!msg.is_encrypted) continue;
        const dec = decryptionMap.get(msg.id);
        if (!dec) continue;
        // #13 FIX: Use encryption_version field instead of unreliable regex
        const isStillEncrypted = msg.encryption_version != null && dec.content === msg.content;
        if (isStillEncrypted) {
          const cached = localCache.get(msg.id);
          if (cached) {
            dec.content = cached.content;
            if (cached.file_key) dec.extraMeta.file_key = cached.file_key;
            if (cached.file_name) dec.extraMeta.file_name = cached.file_name;
            if (cached.file_type) dec.extraMeta.file_type = cached.file_type;
            if (cached.file_size) dec.extraMeta.file_size = cached.file_size;
            if (cached.file_url) dec.extraMeta.file_url = cached.file_url;
            if (cached.duration_seconds) dec.extraMeta.duration_seconds = cached.duration_seconds;
          } else {
            // No local cache and can't decrypt - show "Message" placeholder
            dec.content = 'Message';
          }
        }
      }

      // Gap 6: Derive read_at from other member's last_read_at
      const otherLastReadAt = otherMemberReadResult.data?.last_read_at || null;

      // Apply decrypted content in one batch update
      const decrypted: EncryptedMessage[] = messagesData.map(msg => {
        const dec = decryptionMap.get(msg.id);
        const meta = (msg.metadata || {}) as any;

        let readAt: string | undefined = (msg as any).read_at || undefined;
        if (msg.sender_id === user.id && otherLastReadAt && !readAt) {
          if (new Date(msg.created_at) < new Date(otherLastReadAt)) {
            readAt = otherLastReadAt;
          }
        }

        return {
          ...msg,
          content: dec?.content ?? msg.content,
          file_url: dec?.extraMeta?.file_url || meta.file_url || undefined,
          file_name: dec?.extraMeta?.file_name || meta.file_name || undefined,
          file_type: dec?.extraMeta?.file_type || meta.file_type || undefined,
          file_size: dec?.extraMeta?.file_size || meta.file_size || undefined,
          duration_seconds: dec?.extraMeta?.duration_seconds || meta.duration_seconds || undefined,
          thumbnail_url: meta.thumbnail_url || undefined,
          reply_to_id: meta.reply_to_id || undefined,
          file_key: dec?.extraMeta?.file_key || undefined,
          read_at: readAt,
          reactions: meta.reactions || undefined,
        } as EncryptedMessage;
      });

      setMessages(prev => {
        const fetchedIds = new Set(decrypted.map(m => m.id));
        // Keep any locally-added messages (optimistic or realtime)
        // that the DB fetch hasn't caught up to yet
        const localOnly = prev.filter(m =>
          !fetchedIds.has(m.id) &&
          m.room_id === roomId &&
          (!decrypted.length || m.created_at >= decrypted[decrypted.length - 1].created_at)
        );
        const merged = [...decrypted, ...localOnly].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        messageCache.set(roomId, merged);
        return merged;
      });

      // Cache successfully decrypted messages to IndexedDB (Signal on-device store)
      const toCache: LocalMessage[] = [];
      for (const msg of decrypted) {
        // Only cache messages that were encrypted and successfully decrypted (not raw/placeholder)
        const original = messagesData.find(m => m.id === msg.id);
        if (original?.is_encrypted && msg.content !== 'Message' && msg.content !== original.content) {
          toCache.push({
            id: msg.id,
            room_id: msg.room_id,
            content: msg.content,
            file_url: msg.file_url || undefined,
            file_name: msg.file_name || undefined,
            file_type: msg.file_type || undefined,
            file_size: msg.file_size || undefined,
            file_key: msg.file_key || undefined,
            duration_seconds: msg.duration_seconds || undefined,
          });
        }
      }
      cacheDecryptedMessages(toCache); // fire-and-forget

      // Resolve media URLs in parallel (non-blocking)
      const mediaUpdates: Promise<{ index: number; field: string; url: string } | null>[] = [];
      decrypted.forEach((msg, i) => {
        if (msg.file_url && !msg.file_key) {
          mediaUpdates.push(
            getMediaUrl(msg.file_url).then(url => ({ index: i, field: 'file_url', url })).catch(() => null)
          );
        }
        if (msg.thumbnail_url && !msg.thumbnail_url.startsWith('data:')) {
          mediaUpdates.push(
            getMediaUrl(msg.thumbnail_url).then(url => ({ index: i, field: 'thumbnail_url', url })).catch(() => null)
          );
        }
      });

      if (mediaUpdates.length > 0) {
        const results = await Promise.all(mediaUpdates);
        const updates = results.filter(Boolean) as { index: number; field: string; url: string }[];
        if (updates.length > 0) {
          setMessages(prev => {
            const next = [...prev];
            for (const u of updates) {
              if (next[u.index]) {
                next[u.index] = { ...next[u.index], [u.field]: u.url };
              }
            }
            return next;
          });
        }
      }
    } catch (err) {
      log.error('Failed to fetch messages:', err);
    }
  }, [getSharedSecret, decryptContent]);

  // ─── Create direct room ─────────────────────────────────────

  const createDirectRoom = useCallback(async (otherUserId: string): Promise<ChatRoom | null> => {
    try {
      const user = getUser();
      if (!user) return null;

      // Helper to return an existing room
      const returnExistingRoom = async (roomId: string, profileUserId: string): Promise<ChatRoom | null> => {
        const { data: existingRoom } = await supabase
          .from('chat_rooms')
          .select('*')
          .eq('id', roomId)
          .single();

        if (existingRoom) {
          const chatRoom = existingRoom as ChatRoom;
          const { data: profile } = await supabase
            .from('profiles')
            .select('user_id, full_name, avatar_url, phone_number')
            .eq('user_id', profileUserId)
            .maybeSingle();

          chatRoom.other_user = profile
            ? { user_id: profile.user_id, full_name: profile.full_name, avatar_url: profile.avatar_url, phone_number: profile.phone_number }
            : { user_id: profileUserId, full_name: null, avatar_url: null };

          setCurrentRoom(chatRoom);
          await fetchMessages(chatRoom.id);
          return chatRoom;
        }
        return null;
      };

      const { data: myRooms } = await supabase
        .from('chat_room_members')
        .select('room_id')
        .eq('user_id', user.id);

      if (myRooms && myRooms.length > 0) {
        const myRoomIds = myRooms.map(r => r.room_id);
        
        // 1. Direct user_id match
        const { data: sharedMembership } = await supabase
          .from('chat_room_members')
          .select('room_id')
          .eq('user_id', otherUserId)
          .in('room_id', myRoomIds)
          .limit(1)
          .maybeSingle();

        if (sharedMembership) {
          const result = await returnExistingRoom(sharedMembership.room_id, otherUserId);
          if (result) return result;
        }

        // 2. Phone number match - prevent duplicate rooms for same contact
        const { data: otherProfile } = await supabase
          .from('profiles')
          .select('phone_number')
          .eq('user_id', otherUserId)
          .maybeSingle();

        if (otherProfile?.phone_number) {
          // Find any other user with the same phone who already has a room with us
          const { data: samePhoneUsers } = await supabase
            .from('profiles')
            .select('user_id')
            .eq('phone_number', otherProfile.phone_number)
            .neq('user_id', otherUserId);

          if (samePhoneUsers && samePhoneUsers.length > 0) {
            for (const spu of samePhoneUsers) {
              const { data: match } = await supabase
                .from('chat_room_members')
                .select('room_id')
                .eq('user_id', spu.user_id)
                .in('room_id', myRoomIds)
                .limit(1)
                .maybeSingle();

              if (match) {
                // Found existing room with same phone contact - reuse it
                const result = await returnExistingRoom(match.room_id, spu.user_id);
                if (result) return result;
              }
            }
          }
        }
      }

      const { data: newRoom, error: roomError } = await supabase
        .from('chat_rooms')
        .insert({
          created_by: user.id,
          is_encrypted: true,
          room_type_v2: 'direct',
        })
        .select()
        .single();

      if (roomError || !newRoom) return null;

      await supabase.from('chat_room_members').insert([
        { room_id: newRoom.id, user_id: user.id },
        { room_id: newRoom.id, user_id: otherUserId },
      ]);

      const chatRoom = newRoom as ChatRoom;

      const { data: profile } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, phone_number')
        .eq('user_id', otherUserId)
        .single();

      chatRoom.other_user = profile
        ? { user_id: profile.user_id, full_name: profile.full_name, avatar_url: profile.avatar_url, phone_number: profile.phone_number }
        : { user_id: otherUserId, full_name: null, avatar_url: null };

      setCurrentRoom(chatRoom);
      await fetchRooms();
      return chatRoom;
    } catch (err) {
      log.error('Failed to create room:', err);
      return null;
    }
  }, [fetchMessages, fetchRooms]);

  // ─── Select room ────────────────────────────────────────────

  const selectRoom = useCallback(async (roomId: string) => {
    // Deselect: clear current room and wipe its cache
    if (!roomId) {
      const prevRoomId = currentRoomRef.current?.id;
      setCurrentRoom(null);
      setMessages([]);
      if (prevRoomId) messageCache.delete(prevRoomId);
      return;
    }

    const room = rooms.find(r => r.id === roomId);
    if (room) {
      setCurrentRoom(room);
      setHasMoreMessages(true);
      const cached = messageCache.get(roomId);
      if (cached && cached.length > 0) {
        setMessages(cached);
      }
      fetchMessages(roomId);
    } else {
      // Room not in local state - verify membership BEFORE serving any cached data
      setHasMoreMessages(true);
      // Do NOT serve cached messages until membership is confirmed
      setMessages([]);
      try {
        const user = getUser();
        // Verify current user is actually a member of this room
        const { data: membership } = await supabase
          .from('chat_room_members')
          .select('room_id')
          .eq('room_id', roomId)
          .eq('user_id', user?.id || '')
          .maybeSingle();

        if (!membership) {
          // Not a member - clear any stale cache and abort
          messageCache.delete(roomId);
          setCurrentRoom(null);
          return;
        }

        const { data: roomData } = await supabase
          .from('chat_rooms')
          .select('*')
          .eq('id', roomId)
          .single();
        if (roomData) {
          const { data: otherMember } = await supabase
            .from('chat_room_members')
            .select('user_id')
            .eq('room_id', roomId)
            .neq('user_id', user?.id || '')
            .single();
          let otherUser: ChatRoom['other_user'] = undefined;
          if (otherMember) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('user_id, full_name, avatar_url, phone_number')
              .eq('user_id', otherMember.user_id)
              .single();
            otherUser = profile
              ? { user_id: profile.user_id, full_name: profile.full_name, avatar_url: profile.avatar_url, phone_number: profile.phone_number }
              : { user_id: otherMember.user_id, full_name: null, avatar_url: null };
          }
          const chatRoom: ChatRoom = { ...roomData as any, other_user: otherUser };
          setCurrentRoom(chatRoom);
          fetchMessages(roomId);
        } else {
          messageCache.delete(roomId);
          setCurrentRoom(null);
          setMessages([]);
          await fetchRooms();
        }
      } catch {
        messageCache.delete(roomId);
        setCurrentRoom(null);
        setMessages([]);
        await fetchRooms();
      }
    }
  }, [rooms, fetchMessages, fetchRooms]);

  // ─── Send message (ratchet-first, legacy fallback) ──────────

  const sendMessage = useCallback(async (content: string, options?: Partial<SendMessageOptions> & { targetRoomId?: string }) => {
    const targetRoom = options?.targetRoomId 
      ? rooms.find(r => r.id === options.targetRoomId) || currentRoom
      : currentRoom;
    if (!targetRoom) return;
    const user = getUser();
    if (!user) return;

    try {
      let encryptedContent = content;
      let isEncrypted = false;
      let encryptionVersion: number | null = null;
      let ratchetResult: RatchetEncryptResult | null = null;
      const msgType = options?.messageType || 'text';

      const isMediaMsg = msgType !== 'text' && msgType !== 'system' && msgType !== 'call_event' && options?.fileUrl;

      if (targetRoom.is_encrypted && targetRoom.other_user) {
        // Build the content to encrypt (media payload or plain text)
        let contentToEncrypt = content;
        if (isMediaMsg && options?.fileKey) {
          // #11 FIX: Add "v":"media" discriminator to prevent false-positive parsing
          contentToEncrypt = JSON.stringify({
            v: 'media',
            t: content || '',
            fk: options.fileKey,
            fn: options.fileName || '',
            ft: options.fileType || '',
            fs: options.fileSize || 0,
            d: options.durationSeconds || undefined,
          });
        }

        // Try ratchet encryption first
        const ratchetSession = await getOrCreateRatchetSession(user.id, targetRoom.other_user.user_id);
        
        if (ratchetSession) {
          try {
            ratchetResult = await ratchetEncrypt(ratchetSession, contentToEncrypt);
            encryptedContent = ratchetResult.ciphertext;
            isEncrypted = true;
            encryptionVersion = 2;
          } catch (err) {
            log.error('[DoubleRatchet] Encrypt failed, falling back to legacy:', err);
            ratchetResult = null;
          }
        }

        // Legacy fallback
        if (!ratchetResult) {
          const secret = await getSharedSecret(targetRoom.other_user.user_id);
          if (secret) {
            encryptedContent = await encryptMessage(contentToEncrypt, secret);
            isEncrypted = true;
            encryptionVersion = 1;
          }
        }
      }

      const optimisticMessage: EncryptedMessage = {
        id: crypto.randomUUID(),
        room_id: targetRoom.id,
        sender_id: user.id,
        content,
        created_at: new Date().toISOString(),
        is_encrypted: isEncrypted,
        message_type: msgType,
        file_url: options?.fileUrl,
        file_name: options?.fileName,
        file_size: options?.fileSize,
        file_type: options?.fileType,
        duration_seconds: options?.durationSeconds,
        thumbnail_url: options?.thumbnailUrl,
        reply_to_id: options?.replyToId,
        file_key: options?.fileKey,
        encryption_version: encryptionVersion,
        expires_at: options?.expiresAt || null,
      };

      if (targetRoom.id === currentRoom?.id) {
        setMessages(prev => {
          const next = [...prev, optimisticMessage];
          messageCache.set(targetRoom.id, next);
          return next;
        });
      }

      const insertData: any = {
        room_id: targetRoom.id,
        sender_id: user.id,
        content: encryptedContent,
        is_encrypted: isEncrypted,
        message_type: msgType,
        encryption_version: encryptionVersion,
      };

      // Disappearing messages: set expires_at from room timer
      if (options?.expiresAt) {
        insertData.expires_at = options.expiresAt;
      }

      const metadata: any = { sent_via: 'ELEFA' };
      if (options?.fileUrl) metadata.file_url = options.fileUrl;
      if (!isEncrypted) {
        if (options?.fileName) metadata.file_name = options.fileName;
        if (options?.fileSize) metadata.file_size = options.fileSize;
        if (options?.fileType) metadata.file_type = options.fileType;
        if (options?.durationSeconds) metadata.duration_seconds = options.durationSeconds;
        if (options?.thumbnailUrl) metadata.thumbnail_url = options.thumbnailUrl;
      }
      if (options?.replyToId) metadata.reply_to_id = options.replyToId;
      
      if (Object.keys(metadata).length > 0) {
        insertData.metadata = metadata;
      }

      const { data: insertedMsg, error: insertError } = await supabase
        .from('chat_messages')
        .insert(insertData)
        .select('id')
        .single();

      if (insertError) {
        log.error('Failed to send message:', insertError);
        // Queue for offline retry if network error
        if (!navigator.onLine || insertError.message?.includes('network') || insertError.message?.includes('fetch')) {
          // Capture auth token for background sync replay
          const { data: { session } } = await supabase.auth.getSession();
          const pendingMsg: PendingMessage = {
            local_id: optimisticMessage.id,
            room_id: targetRoom.id,
            encrypted_payload: encryptedContent,
            plaintext_content: content,
            message_type: msgType,
            metadata: insertData.metadata || {},
            created_at: optimisticMessage.created_at,
            retry_count: 0,
            status: 'pending',
            encryption_version: encryptionVersion,
            auth_token: session?.access_token,
            supabase_url: import.meta.env.VITE_SUPABASE_URL,
            supabase_anon_key: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          };
          await queueMessage(pendingMsg);
          // Keep optimistic message visible with pending indicator
          setMessages(prev => prev.map(m =>
            m.id === optimisticMessage.id ? { ...m, message_type: m.message_type + '|pending' } : m
          ));
          log.debug('[OfflineQueue] Message queued for background sync');
        } else {
          setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
        }
        return;
      }

      // Cache the plaintext locally (Signal on-device store)
      if (isEncrypted && insertedMsg) {
        cacheDecryptedMessages([{
          id: insertedMsg.id,
          room_id: targetRoom.id,
          content,
          file_url: options?.fileUrl,
          file_name: options?.fileName,
          file_type: options?.fileType,
          file_size: options?.fileSize,
          file_key: options?.fileKey,
          duration_seconds: options?.durationSeconds,
        }]);
      }

      // Store ratchet header if using double ratchet
      if (ratchetResult && insertedMsg) {
        const ratchetPubKey = await getCurrentRatchetPublicKey(
          await getRatchetSession(user.id, targetRoom.other_user!.user_id),
        );

        await supabase.from('ratchet_message_headers').insert({
          message_id: insertedMsg.id,
          ratchet_public_key: ratchetPubKey || '',
          previous_chain_length: 0,
          message_number: 0,
        });
      }
    } catch (err) {
      log.error('Failed to send message:', err);
    }
  }, [currentRoom, rooms, getSharedSecret, getOrCreateRatchetSession, getRatchetSession]);

  // ─── Mark as read ───────────────────────────────────────────
  // markedReadRoomsRef is declared earlier (before fetchRooms) so the
  // poll-protection guard in fetchRooms can reference it.

  const markAsRead = useCallback(async (roomId: string) => {
    const user = getUser();
    if (!user) return;

    // Immediately clear local badge & protect from poll overwrite
    markedReadRoomsRef.current.add(roomId);
    setRooms(prev => prev.map(room =>
      room.id === roomId ? { ...room, unread_count: 0 } : room
    ));

    const now = new Date().toISOString();

    // Use a simple update without .select() to avoid RLS complications
    const { error, status } = await supabase
      .from('chat_room_members')
      .update({ last_read_at: now })
      .eq('room_id', roomId)
      .eq('user_id', user.id);

    if (error) {
      log.error('[markAsRead] DB update failed:', error.message);
      // Fallback: upsert
      await supabase
        .from('chat_room_members')
        .upsert(
          { room_id: roomId, user_id: user.id, last_read_at: now },
          { onConflict: 'room_id,user_id' }
        );
    }

    // Keep protection for 60s so polls don't overwrite before DB catches up
    setTimeout(() => {
      markedReadRoomsRef.current.delete(roomId);
    }, 60000);
  }, []);

  // ─── Fetch older messages (cursor-based pagination) ─────────

  const fetchOlderMessages = useCallback(async (roomId: string) => {
    if (isLoadingMore || !hasMoreMessages) return;
    const user = getUser();
    if (!user) return;

    setIsLoadingMore(true);
    try {
      // Get the oldest message timestamp currently loaded
      const currentMessages = messages;
      if (currentMessages.length === 0) { setIsLoadingMore(false); return; }
      
      const oldestTimestamp = currentMessages[0]?.created_at;
      if (!oldestTimestamp) { setIsLoadingMore(false); return; }

      // Fetch older messages using cursor
      const { data: olderData, error: fetchError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('room_id', roomId)
        .lt('created_at', oldestTimestamp)
        .order('created_at', { ascending: false })
        .limit(30);

      if (fetchError || !olderData) {
        log.error('[Pagination] Fetch failed:', fetchError);
        setIsLoadingMore(false);
        return;
      }

      // Check if there are more messages
      if (olderData.length < 30) {
        setHasMoreMessages(false);
      }

      if (olderData.length === 0) {
        setIsLoadingMore(false);
        return;
      }

      // Reverse to ascending order
      olderData.reverse();

      // Get other member for decryption
      const { data: otherMember } = await supabase
        .from('chat_room_members')
        .select('user_id')
        .eq('room_id', roomId)
        .neq('user_id', user.id)
        .single();

      const otherUserId = otherMember?.user_id;
      let legacySecret: CryptoKey | null = null;
      if (otherMember) {
        legacySecret = await getSharedSecret(otherMember.user_id);
      }

      // Fetch local cache for fallback
      const encryptedIds = olderData.filter(m => m.is_encrypted).map(m => m.id);
      const localCache = await getCachedMessagesMap(encryptedIds);

      // Decrypt messages
      const decryptionMap = new Map<string, { content: string; extraMeta: Record<string, any> }>();

      const legacyMsgs = olderData.filter(m => m.is_encrypted && m.encryption_version !== 2);
      const ratchetMsgs = olderData.filter(m => m.is_encrypted && m.encryption_version === 2);

      if (legacyMsgs.length > 0 && otherUserId) {
        const results = await Promise.all(
          legacyMsgs.map(msg =>
            decryptContent(msg, otherUserId, legacySecret, user.id)
              .catch(() => ({ content: msg.content, extraMeta: {} }))
          )
        );
        legacyMsgs.forEach((msg, i) => decryptionMap.set(msg.id, results[i]));
      }

      for (const msg of ratchetMsgs) {
        if (!otherUserId) { decryptionMap.set(msg.id, { content: msg.content, extraMeta: {} }); continue; }
        try {
          const result = await decryptContent(msg, otherUserId, legacySecret, user.id);
          decryptionMap.set(msg.id, result);
        } catch {
          decryptionMap.set(msg.id, { content: msg.content, extraMeta: {} });
        }
      }

      // Apply local cache fallback
      for (const msg of olderData) {
        if (!msg.is_encrypted) continue;
        const dec = decryptionMap.get(msg.id);
        if (!dec) continue;
        // #13 FIX: Use encryption_version field instead of unreliable regex
        const looksEncrypted = msg.encryption_version != null && dec.content === msg.content;
        if (looksEncrypted) {
          const cached = localCache.get(msg.id);
          if (cached) {
            dec.content = cached.content;
            if (cached.file_key) dec.extraMeta.file_key = cached.file_key;
            if (cached.file_name) dec.extraMeta.file_name = cached.file_name;
            if (cached.file_type) dec.extraMeta.file_type = cached.file_type;
            if (cached.file_size) dec.extraMeta.file_size = cached.file_size;
            if (cached.file_url) dec.extraMeta.file_url = cached.file_url;
            if (cached.duration_seconds) dec.extraMeta.duration_seconds = cached.duration_seconds;
          } else {
            dec.content = 'Message';
          }
        }
      }

      // Build decrypted messages
      const decrypted: EncryptedMessage[] = olderData.map(msg => {
        const dec = decryptionMap.get(msg.id);
        const meta = (msg.metadata || {}) as any;
        return {
          ...msg,
          content: dec?.content ?? msg.content,
          file_url: dec?.extraMeta?.file_url || meta.file_url || undefined,
          file_name: dec?.extraMeta?.file_name || meta.file_name || undefined,
          file_type: dec?.extraMeta?.file_type || meta.file_type || undefined,
          file_size: dec?.extraMeta?.file_size || meta.file_size || undefined,
          duration_seconds: dec?.extraMeta?.duration_seconds || meta.duration_seconds || undefined,
          thumbnail_url: meta.thumbnail_url || undefined,
          reply_to_id: meta.reply_to_id || undefined,
          file_key: dec?.extraMeta?.file_key || undefined,
        } as EncryptedMessage;
      });

      // Prepend to existing messages, deduplicate by id
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const newMsgs = decrypted.filter(m => !existingIds.has(m.id));
        const merged = [...newMsgs, ...prev];
        messageCache.set(roomId, merged);
        return merged;
      });

      // Cache to IndexedDB
      const toCache: LocalMessage[] = [];
      for (const msg of decrypted) {
        const original = olderData.find(m => m.id === msg.id);
        if (original?.is_encrypted && msg.content !== 'Message' && msg.content !== original.content) {
          toCache.push({
            id: msg.id,
            room_id: msg.room_id,
            content: msg.content,
            file_url: msg.file_url || undefined,
            file_name: msg.file_name || undefined,
            file_type: msg.file_type || undefined,
            file_size: msg.file_size || undefined,
            file_key: msg.file_key || undefined,
            duration_seconds: msg.duration_seconds || undefined,
          });
        }
      }
      cacheDecryptedMessages(toCache);
    } catch (err) {
      log.error('[Pagination] Failed:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [messages, isLoadingMore, hasMoreMessages, getSharedSecret, decryptContent]);

  // ─── Offline queue flush ────────────────────────────────────

  const flushOfflineQueue = useCallback(async () => {
    const pending = await getAllPending();
    if (pending.length === 0) return;

    log.debug(`[OfflineQueue] Flushing ${pending.length} pending messages`);

    for (const msg of pending) {
      if (msg.retry_count >= MAX_RETRIES) {
        await updatePendingStatus(msg.local_id, 'failed', msg.retry_count);
        continue;
      }

      await updatePendingStatus(msg.local_id, 'sending', msg.retry_count);

      try {
      // B1: Re-encrypt using current ratchet state instead of stale ciphertext
      const user = getUser();
      let encryptedPayload = msg.encrypted_payload;
      if (user && msg.plaintext_content) {
        try {
          // Find room's other user
          const { data: otherMember } = await supabase
            .from('chat_room_members')
            .select('user_id')
            .eq('room_id', msg.room_id)
            .neq('user_id', user.id)
            .single();
          if (otherMember) {
            const ratchetSession = await getOrCreateRatchetSession(user.id, otherMember.user_id);
            if (ratchetSession) {
              const result = await ratchetEncrypt(ratchetSession, msg.plaintext_content);
              encryptedPayload = result.ciphertext;
              log.debug('[OfflineQueue] Re-encrypted with current ratchet state');
            }
          }
        } catch (reencryptErr) {
          log.warn('[OfflineQueue] Re-encryption failed, using original payload:', reencryptErr);
        }
      }

      const insertData: any = {
        room_id: msg.room_id,
        sender_id: user?.id,
        content: encryptedPayload,
        is_encrypted: true,
        message_type: msg.message_type,
        encryption_version: msg.encryption_version,
        metadata: { ...msg.metadata, local_id: msg.local_id },
      };

        const { error } = await supabase
          .from('chat_messages')
          .insert(insertData);

        if (error) throw error;

        await removePending(msg.local_id);
        // Remove pending indicator from UI
        setMessages(prev => prev.map(m =>
          m.id === msg.local_id
            ? { ...m, message_type: m.message_type?.replace('|pending', '') || 'text' }
            : m
        ));
        log.debug(`[OfflineQueue] Sent ${msg.local_id}`);
      } catch (err) {
        const newRetry = msg.retry_count + 1;
        await updatePendingStatus(msg.local_id, 'pending', newRetry);
        
        // Exponential backoff for next retry
        const delay = getBackoffDelay(newRetry);
        setTimeout(() => flushOfflineQueue(), delay);
        log.warn(`[OfflineQueue] Retry ${newRetry}/${MAX_RETRIES} for ${msg.local_id} in ${delay}ms`);
        break; // Stop processing to respect backoff
      }
    }
  }, [getOrCreateRatchetSession]);

  // Flush offline queue when network comes back online
  useEffect(() => {
    if (!enabled) return;
    const handleOnline = () => {
      log.debug('[OfflineQueue] Network online - flushing queue');
      flushOfflineQueue();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [flushOfflineQueue]);

  // ─── Stable refs for realtime handlers (avoid dependency churn) ──
  // currentRoomRef already declared above (before fetchRooms)
  const getSharedSecretRef = useRef(getSharedSecret);
  getSharedSecretRef.current = getSharedSecret;
  const decryptContentRef = useRef(decryptContent);
  decryptContentRef.current = decryptContent;
  const fetchMessagesRef = useRef(fetchMessages);
  fetchMessagesRef.current = fetchMessages;

  // ─── Realtime subscription ──────────────────────────────────
  // IMPORTANT: Only depend on currentRoom?.id to avoid constant re-subscriptions
  // when the room object reference changes but the room ID stays the same.

  const currentRoomId = currentRoom?.id ?? null;

  useEffect(() => {
    if (!enabled || !currentRoomId) return;

    const roomId = currentRoomId;
    log.debug('[E2EE-RT] Setting up realtime + polling for room:', roomId.slice(0, 8));

    const { unsubscribe } = createManagedChannel(
      `room-${roomId}`,
      (ch) => ch.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`,
        },
        async (payload) => {
          const newMessage = payload.new as any;
          const user = getUser();
          if (newMessage.sender_id === user?.id) return;
          // Skip deleted or expired messages from realtime
          if (newMessage.is_deleted) return;
          if (newMessage.expires_at && new Date(newMessage.expires_at) <= new Date()) return;
          // #1 FIX: Use messagesRef to avoid stale closure dedup
          if (messagesRef.current.some(m => m.id === newMessage.id)) return;

          const room = currentRoomRef.current;
          const otherUser = room?.other_user;

          try {
            let content = newMessage.content;
            let extraMeta: Record<string, any> = {};
            
            if (newMessage.is_encrypted && otherUser && user) {
              const legacySecret = await getSharedSecretRef.current(otherUser.user_id);
              const result = await decryptContentRef.current(
                newMessage,
                otherUser.user_id,
                legacySecret,
                user.id,
              );
              content = result.content;
              extraMeta = result.extraMeta;
            }

            const meta = (newMessage.metadata || {}) as any;
            
            let resolvedFileUrl = extraMeta.file_url || meta.file_url || undefined;
            if (resolvedFileUrl && !extraMeta.file_key) {
              try {
                resolvedFileUrl = await getMediaUrl(resolvedFileUrl);
              } catch { /* keep original */ }
            }

            const incomingMsg: EncryptedMessage = {
              ...newMessage,
              content,
              file_url: resolvedFileUrl,
              file_name: extraMeta.file_name || meta.file_name || undefined,
              file_type: extraMeta.file_type || meta.file_type || undefined,
              file_size: extraMeta.file_size || meta.file_size || undefined,
              duration_seconds: extraMeta.duration_seconds || meta.duration_seconds || undefined,
              file_key: extraMeta.file_key || undefined,
              reactions: meta.reactions || undefined,
            };
            setMessages(prev => {
              if (prev.some(m => m.id === newMessage.id)) return prev;
              const next = [...prev, incomingMsg];
              messageCache.set(roomId, next);
              return next;
            });

            if (newMessage.is_encrypted && content !== newMessage.content) {
              cacheDecryptedMessages([{
                id: newMessage.id,
                room_id: roomId,
                content,
                file_url: extraMeta.file_url,
                file_name: extraMeta.file_name,
                file_type: extraMeta.file_type,
                file_size: extraMeta.file_size,
                file_key: extraMeta.file_key,
                duration_seconds: extraMeta.duration_seconds,
              }]);
            }
          } catch (err) {
            log.error('[Realtime] Message handler error:', err);
            setMessages(prev => {
              if (prev.some(m => m.id === newMessage.id)) return prev;
              return [...prev, { ...newMessage, content: newMessage.content }];
            });
          }
        }
      )
      // ── UPDATE listener: deletions + reactions real-time sync ──
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          setMessages(prev => {
            const next = updated.is_deleted
              ? prev.filter(m => m.id !== updated.id)
              : prev.map(m => {
                  if (m.id !== updated.id) return m;
                  const newReactions = (updated.metadata as any)?.reactions;
                  if (newReactions) {
                    return { ...m, reactions: newReactions };
                  }
                  return m;
                });
            messageCache.set(roomId, next);
            return next;
          });
        }
      ),
    );

    // Flush offline queue when channel reconnects
    flushOfflineQueue();

    // 3s incremental polling fallback - catches anything realtime misses
    const pollTimer = setInterval(async () => {
      const user = getUser();
      if (!user) return;

      const currentMsgs = messagesRef.current;
      const latestTimestamp = currentMsgs.length > 0
        ? currentMsgs[currentMsgs.length - 1].created_at
        : null;

      if (!latestTimestamp) {
        // No messages yet - do a full fetch once
        fetchMessagesRef.current(roomId);
        return;
      }

      try {
        const { data: newData, error: pollError } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('room_id', roomId)
          .not('is_deleted', 'is', 'true')
          .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
          .gt('created_at', latestTimestamp)
          .order('created_at', { ascending: true });

        if (pollError || !newData || newData.length === 0) return;

        const room = currentRoomRef.current;
        const otherUserId = room?.other_user?.user_id;
        let legacySecret: CryptoKey | null = null;
        if (otherUserId) {
          legacySecret = await getSharedSecretRef.current(otherUserId);
        }

        // Fetch local cache for fallback
        const encryptedIds = newData.filter(m => m.is_encrypted).map(m => m.id);
        const localCache = encryptedIds.length > 0 ? await getCachedMessagesMap(encryptedIds) : new Map();

        const decryptedNew: EncryptedMessage[] = [];

        for (const msg of newData) {
          // Skip if already in state (dedup)
          if (messagesRef.current.some(m => m.id === msg.id)) continue;
          // Skip own messages (already added optimistically)
          if (msg.sender_id === user.id) continue;

          let content = msg.content;
          let extraMeta: Record<string, any> = {};

          if (msg.is_encrypted && otherUserId) {
            try {
              const result = await decryptContentRef.current(msg, otherUserId, legacySecret, user.id);
              content = result.content;
              extraMeta = result.extraMeta;
              // Check if still encrypted, fall back to cache
              if (msg.encryption_version != null && content === msg.content) {
                const cached = localCache.get(msg.id);
                if (cached) {
                  content = cached.content;
                  if (cached.file_key) extraMeta.file_key = cached.file_key;
                  if (cached.file_name) extraMeta.file_name = cached.file_name;
                  if (cached.file_type) extraMeta.file_type = cached.file_type;
                  if (cached.file_size) extraMeta.file_size = cached.file_size;
                  if (cached.file_url) extraMeta.file_url = cached.file_url;
                } else {
                  content = 'Message';
                }
              }
            } catch {
              content = msg.content;
            }
          }

          const meta = (msg.metadata || {}) as any;
          decryptedNew.push({
            ...msg,
            content,
            file_url: extraMeta.file_url || meta.file_url || undefined,
            file_name: extraMeta.file_name || meta.file_name || undefined,
            file_type: extraMeta.file_type || meta.file_type || undefined,
            file_size: extraMeta.file_size || meta.file_size || undefined,
            duration_seconds: extraMeta.duration_seconds || meta.duration_seconds || undefined,
            thumbnail_url: meta.thumbnail_url || undefined,
            reply_to_id: meta.reply_to_id || undefined,
            file_key: extraMeta.file_key || undefined,
          } as EncryptedMessage);
        }

        if (decryptedNew.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const unique = decryptedNew.filter(m => !existingIds.has(m.id));
            if (unique.length === 0) return prev;
            const next = [...prev, ...unique];
            messageCache.set(roomId, next);
            return next;
          });
        }
      } catch (err) {
        log.error('[Poll] Incremental fetch failed:', err);
      }
    }, 3000);

    return () => {
      unsubscribe();
      clearInterval(pollTimer);
    };
  }, [enabled, currentRoomId]);

  // ─── Gap 1+2: Global room-level subscription ────────────────
  // Listens for new messages across ALL rooms (independent of currentRoom).
  // Updates room list and triggers browser notifications when tab is hidden.

  // Stable refs for rooms - avoids re-subscription on every room list change
  const roomsRef = useRef(rooms);
  roomsRef.current = rooms;
  // currentRoomRef already declared above

  // Debounced fetchRooms to prevent subscription churn
  const debouncedFetchRoomsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedFetchRooms = useCallback(() => {
    if (debouncedFetchRoomsTimer.current) clearTimeout(debouncedFetchRoomsTimer.current);
    debouncedFetchRoomsTimer.current = setTimeout(() => {
      fetchRooms();
    }, 300);
  }, [fetchRooms]);

  // #4 FIX: Removed global chat subscription (subscribed to ALL rooms system-wide).
  // Replaced with per-user chat_room_members listener + 60s polling (already exists below).
  // The per-room channel (line ~1551) handles active room messages.
  // The global-room-members channel (line ~1775) handles new room detection.
  useEffect(() => {
    if (!enabled) return;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      const user = userRef.current;
      if (!user) {
        const retryTimer = setTimeout(startPolling, 500);
        return () => clearTimeout(retryTimer);
      }

      // Poll rooms every 10s for unread badge updates (fast sync like WhatsApp)
      pollTimer = setInterval(() => {
        fetchRooms();
      }, 10000);
    };

    startPolling();

    return () => {
      if (pollTimer) clearInterval(pollTimer);
      if (debouncedFetchRoomsTimer.current) clearTimeout(debouncedFetchRoomsTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRooms]);

  // ─── Gap 7: Listen for new room memberships ─────────────────
  // Detects when another user creates a room with the current user.

  useEffect(() => {
    if (!enabled) return;
    const user = userRef.current;
    if (!user) return;

    const { unsubscribe } = createManagedChannel(
      'global-room-members',
      (ch) => ch.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_room_members',
          filter: `user_id=eq.${user.id}`,
        },
        async () => {
          // New room membership - refresh room list
          await fetchRooms();
        }
      ),
    );

    return () => { unsubscribe(); };
  }, [fetchRooms]);

  // ─── Global message listener for browser notifications ──────
  // Listens for new messages across ALL rooms the user is in.
  // Triggers browser push notifications when tab is hidden (WhatsApp-style).
  // Also refreshes room list to update unread badges instantly.

  useEffect(() => {
    if (!enabled) return;
    const user = userRef.current;
    if (!user) return;

    // Request notification permission early
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const { unsubscribe } = createManagedChannel(
      'global-chat-notifications',
      (ch) => ch.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        async (payload) => {
          const msg = payload.new as any;
          // Skip own/deleted/expired messages
          if (msg.sender_id === user.id) return;
          if (msg.is_deleted) return;
          if (msg.expires_at && new Date(msg.expires_at) <= new Date()) return;
          // Skip system/call_event messages
          if (msg.message_type === 'system' || msg.message_type === 'call_event') return;

          // Verify this message belongs to a room the user is in
          const currentRooms = roomsRef.current;
          const room = currentRooms.find(r => r.id === msg.room_id);
          if (!room) return;

          // If this message is for the currently active room, skip notification
          // (the per-room handler already handles it)
          const activeRoomId = currentRoomRef.current?.id;
          if (msg.room_id === activeRoomId && !document.hidden) return;

          // Refresh room list to update unread badges immediately
          debouncedFetchRooms();

          // Trigger browser notification
          const senderName = room.other_user?.full_name || room.other_user?.phone_number || 'New message';
          const preview = msg.is_encrypted
            ? getMessageTypePreview(msg.message_type)
            : (msg.content?.substring(0, 80) || getMessageTypePreview(msg.message_type));

          showMessageNotification(
            senderName,
            preview,
            msg.room_id,
          );
        }
      ),
    );

    return () => { unsubscribe(); };
  }, [debouncedFetchRooms]);


  // When a contact's identity key changes, invalidate cached shared secrets
  // and show a "Security code changed" system message (Signal-style).

  useEffect(() => {
    if (!enabled || !currentRoom?.other_user?.user_id) return;
    const otherUserId = currentRoom.other_user.user_id;

    // #10 FIX: Use createManagedChannel for auto-reconnect on WebSocket drop
    const { unsubscribe } = createManagedChannel(
      `key-change-${otherUserId}`,
      (ch) => ch.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'encryption_keys',
          filter: `user_id=eq.${otherUserId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          const cachedVersion = keyVersionCache.get(otherUserId);

          if (cachedVersion !== undefined && updated.key_version > cachedVersion) {
            log.warn('[E2EE] SECURITY: Contact key changed - invalidating session');

            // Invalidate caches
            sharedSecretCache.delete(otherUserId);
            keyVersionCache.delete(otherUserId);
            ratchetSessionCache.delete(`${userRef.current?.id}::${otherUserId}`);

            // Clear verification status from IndexedDB
            try {
              const dbReq = indexedDB.open('elefa-safety-numbers', 1);
              dbReq.onsuccess = () => {
                try {
                  const db = dbReq.result;
                  const tx = db.transaction('verified', 'readwrite');
                  const key = [userRef.current?.id, otherUserId].sort().join(':');
                  tx.objectStore('verified').delete(key);
                } catch { /* non-critical */ }
              };
            } catch { /* non-critical */ }

            // Show security alert in chat
            const systemMsg: EncryptedMessage = {
              id: crypto.randomUUID(),
              room_id: currentRoom.id,
              sender_id: 'system',
              content: '🔐 Security code changed. Verify this contact to ensure your messages are secure.',
              created_at: new Date().toISOString(),
              is_encrypted: false,
              message_type: 'system',
            };
            setMessages(prev => [...prev, systemMsg]);
          }
        }
      ),
    );

    return () => { unsubscribe(); };
  }, [currentRoom?.id, currentRoom?.other_user?.user_id]);

  // ─── Initial load ───────────────────────────────────────────

  useEffect(() => {
    if (!enabled) { setIsLoading(false); return; }
    const init = async () => {
      // Clean up old un-namespaced cache key (one-time migration)
      localStorage.removeItem('elefa-chat-rooms-cache');

      // Use getSession (localStorage, no network) instead of getUser (network call)
      if (!userRef.current) {
        const { data } = await supabase.auth.getSession();
        userRef.current = data.session?.user ?? null;
      }
      if (!userRef.current) {
        setIsLoading(false);
        return;
      }

      // Load user-specific cache for instant display
      const cached = getCachedRooms(userRef.current.id);
      if (cached.length > 0) {
        setRooms(cached);
        setIsLoading(false);
      }

      // Fetch rooms immediately (show UI fast) - don't await encryption
      fetchRooms().then(() => setIsLoading(false));

      // Initialize encryption fully in background - never blocks room list
      initializeEncryption();
    };

    init();
  }, [initializeEncryption, fetchRooms]);

  // ─── Client-side disappearing message removal (Signal-style) ───
  useEffect(() => {
    if (!enabled) return;
    const now = Date.now();
    let soonestMs = Infinity;

    const activeExpiries = messages.filter(m => m.expires_at);
    if (activeExpiries.length === 0) return;

    for (const m of activeExpiries) {
      const expiresMs = new Date(m.expires_at!).getTime() - now;
      if (expiresMs < soonestMs) soonestMs = expiresMs;
    }

    // If already past, remove immediately
    const delay = Math.max(soonestMs, 0);

    const timer = setTimeout(() => {
      const cutoff = Date.now();
      setMessages(prev => {
        const filtered = prev.filter(m => {
          if (!m.expires_at) return true;
          return new Date(m.expires_at).getTime() > cutoff;
        });
        // Update cache too
        if (currentRoom) {
          messageCache.set(currentRoom.id, filtered);
        }
        return filtered;
      });
    }, delay);

    return () => clearTimeout(timer);
  }, [messages, currentRoom]);

  // When disabled (e.g. Twilio is active provider), return no-op to prevent
  // any state leaks from the dormant hooks above
  if (!enabled) return noopReturn;

  return {
    rooms,
    messages,
    isLoading,
    error,
    currentRoom,
    sendMessage,
    createDirectRoom,
    selectRoom,
    markAsRead,
    initializeEncryption,
    isEncryptionReady,
    fetchOlderMessages,
    hasMoreMessages,
    isLoadingMore,
    needsRecovery,
    setNeedsRecovery,
  };
}

// ─── Helper functions ───────────────────────────────────────


function parseDecryptedContent(
  raw: string,
  msg: any,
): { content: string; extraMeta: Record<string, any> } {
  // #11 FIX: Check for "v":"media" discriminator first, fallback to legacy "fk" check
  if (raw.startsWith('{') && (raw.includes('"v":"media"') || raw.includes('"fk"'))) {
    try {
      const payload = JSON.parse(raw);
      const meta = (msg.metadata || {}) as any;
      return {
        content: payload.t || '',
        extraMeta: {
          file_key: payload.fk,
          file_name: payload.fn || meta.file_name,
          file_type: payload.ft || meta.file_type,
          file_size: payload.fs || meta.file_size,
          duration_seconds: payload.d || meta.duration_seconds,
        },
      };
    } catch { /* fall through */ }
  }
  return { content: raw, extraMeta: {} };
}

async function getCurrentRatchetPublicKey(
  session: AsymmetricRatchet | null,
): Promise<string | null> {
  if (!session?.currentRatchetKey?.publicKey) return null;
  try {
    const pubKey = session.currentRatchetKey.publicKey;
    // ECPublicKey has a .key property that is a CryptoKey
    const raw = await crypto.subtle.exportKey('raw', (pubKey as any).key || pubKey);
    const bytes = new Uint8Array(raw);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  } catch {
    return null;
  }
}

async function buildPreKeyBundle(preKeyData: any): Promise<PreKeyBundleProtocol> {
  // signed_prekey stores the full serialized PreKeyBundleProtocol as base64
  const bundle = await importPreKeyBundleBlob(preKeyData.signed_prekey);
  return bundle;
}
